/**
 * Creates a shallow clone of an array while preserving the array length and indices.
 * This method is more performant than Array.from() as it avoids
 * the creation of intermediate array copies and uses a pre-allocated array.
 *
 * Performance advantages over Array.from():
 * - Pre-allocates the result array with the exact size needed
 * - Directly assigns elements without intermediate operations
 * - Avoids the overhead of Array.from's iterator protocol
 *
 * @param arr - The source array to clone
 * @returns A new array containing the same elements as the input array
 *
 * @example
 * const original = [1, 2, 3];
 * const cloned = arrayShallowClone(original);
 * // cloned will be [1, 2, 3] but a different array reference
 *
 * @example
 * const readonlyArr: readonly number[] = [1, 2, 3];
 * const mutableCopy = arrayShallowClone(readonlyArr);
 * // Now mutableCopy can be modified
 */
export function arrayShallowClone<T>(arr: readonly T[] | undefined | null): T[] {
  return (
    arr?.reduce((result, next, index) => {
      result[index] = next;
      return result;
    }, new Array<T>(arr.length)) ?? []
  );
}

/**
 * Creates a new array with unique elements from multiple input arrays, preserving the original order
 * of first occurrence across all arrays. This implementation uses a Set for O(1) lookups, making it
 * more efficient than array-based deduplication methods.
 *
 * Performance advantages:
 * - More efficient than using Array.filter() with indexOf() which has O(n²) complexity
 * - Better than [...new Set(arr)] as it preserves the type information
 * - Maintains original order unlike Object.keys() based solutions
 * - Memory efficient as it only creates one Set and one result array
 *
 * @param arrays - Rest parameter of arrays to be processed. Each array may contain duplicate elements
 * @returns A new array containing only unique elements in their original order of appearance across all input arrays
 *
 * @example
 * const array1 = [1, 2, 2];
 * const array2 = [2, 3, 1];
 * const array3 = [3, 4, 5];
 * const unique = arrayUnique(array1, array2, array3);
 * // Returns [1, 2, 3, 4, 5]
 *
 * @remarks
 * - For object arrays, uniqueness is determined by reference equality.
 * - If you need to deduplicate objects based on their properties,
 *   you should implement a custom solution or transform the objects first.
 * - Handles null/undefined gracefully by treating them as empty arrays
 * - The function processes arrays sequentially, so elements in earlier arrays
 *   take precedence over duplicates in later arrays
 */
export function arrayUnique<T>(...arrays: (readonly T[] | undefined | null)[]): T[] {
  const set = new Set<T>();
  const fn = (result: T[], next: T): T[] => {
    if (!set.has(next)) {
      result.push(next);
      set.add(next);
    }
    return result;
  };
  return arrays?.reduce((resultArray: T[], nextArray) => nextArray?.reduce(fn, resultArray) ?? resultArray, new Array<T>()) ?? [];
}
